import {Component, computed, DestroyRef, OnDestroy, OnInit, inject, input, output, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';


import {BreakpointObserver} from "@angular/cdk/layout";
import {RoverAuthenticationCredentials, RoverService} from "src/app/services/agents/rover.service";
import {catchError, EMPTY, map, Observable, switchMap, timer} from "rxjs";
import {CurrentTenantStore} from "src/app/services/tenant/current-tenant.store";
import {tap} from "rxjs/operators";
import {TenantService} from "src/app/services/tenant/tenant.service";
import * as Bowser from "bowser";
import {TranslatePipe} from "@ngx-translate/core";
import {IconButtonComponent} from 'src/app/modules/design-system/icon-button/icon-button.component';
import {TextButtonComponent} from 'src/app/modules/design-system/text-button/text-button.component';

@Component({
  selector: 'app-onboarding-step-4',
  standalone: true,
	imports: [IconButtonComponent, TextButtonComponent, TranslatePipe],
  templateUrl: './onboarding-step-4.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../onboarding.component.scss'
})
export class OnboardingStep4Component implements OnInit, OnDestroy {
	private roverService = inject(RoverService);
	// Also runs outside the shell, right after step 3 created the workspace: wait until it is loaded.
	private tenantStore = inject(CurrentTenantStore);
	private tenantService = inject(TenantService);
	private destroyRef = inject(DestroyRef);

	readonly stepCount = input.required<number>();

	readonly onNextClick = output<string>();
	readonly onDismiss = output<undefined>();

	private tenantId: string;

	closeLoading = false;

	private readonly compact = toSignal(inject(BreakpointObserver).observe(['(max-width: 1280px)']).pipe(map(state => state.matches)), {initialValue: false});
	protected readonly panelClass = computed(() => this.compact() ? '' : 'big-button');

	ngOnInit() {
		this.tenantStore.whenLoaded()
			.pipe(
				map(tenant => tenant.configuration.id),
				tap(tenantId => this.tenantId = tenantId),
				switchMap(tenantId => this.roverService.getRoverAuthenticationCredentials(tenantId)),
				tap((rover) => this.setRoverAuthenticationCredentials(rover)),
				switchMap(() => this.checkIfExtensionInstalled()),
				takeUntilDestroyed(this.destroyRef))
			.subscribe((isInstalled: boolean) => {
				if (isInstalled) {
					this.onNextClick.emit('Step 5');
				}
			});
	}

	setRoverAuthenticationCredentials(credentials: RoverAuthenticationCredentials): void {
		const structureEltId = 'kabeen-structure-id';
		const teamEltId = 'kabeen-team-id';
		const tokenEltId = 'kabeen-token-id';
		const structure = document.createElement('div');
		const team = document.createElement('div');
		const token = document.createElement('div');
		structure.id = structureEltId;
		team.id = teamEltId;
		token.id = tokenEltId;
		structure.style.display = 'none';
		team.style.display = 'none';
		token.style.display = 'none';
		const structureData = document.createAttribute('data-kabeen-structure-id');
		const teamData = document.createAttribute('data-kabeen-team-id');
		const tokenData = document.createAttribute('data-kabeen-token-id');
		structureData.value = credentials.structureId;
		teamData.value = credentials.teamId;
		tokenData.value = credentials.token;
		structure.setAttributeNode(structureData);
		team.setAttributeNode(teamData);
		token.setAttributeNode(tokenData);
		document.body.appendChild(structure);
		document.body.appendChild(team);
		document.body.appendChild(token);
	}

	/** Polls every 2 seconds whether the extension is installed. A failed poll skips the tick instead of stopping. */
	private checkIfExtensionInstalled(): Observable<boolean> {
		return timer(0, 2000)
			.pipe(switchMap(() => this.tenantService.checkTenantExtensionInstalled(this.tenantId)
				.pipe(catchError(() => EMPTY))));
	}

	openBrowserCorrespondingStore(): void {
		const browser = Bowser.getParser(window.navigator.userAgent)
		const browserName: string = browser.getBrowserName();
		if (browserName === 'Chrome')
			window.open('https://chromewebstore.google.com/detail/kabeen/hkmgmmaopoofgchofbngplopnhajbojc?hl=fr', '_blank');
		else if (browserName === 'Firefox')
			window.open('https://addons.mozilla.org/fr/firefox/addon/kabeen/', '_blank');
		else if (browserName === 'Microsoft Edge')
			window.open('https://microsoftedge.microsoft.com/addons/detail/kabeen/jgdflahjneomipldehpneckiplknghbc?hl=en-US', '_blank');
		else
			window.open('https://chromewebstore.google.com/detail/kabeen/hkmgmmaopoofgchofbngplopnhajbojc?hl=fr', '_blank');
	}

	ngOnDestroy() {
		const structure = document.getElementById('kabeen-structure-id');
		const team = document.getElementById('kabeen-team-id');
		const token = document.getElementById('kabeen-token-id');
		if (structure) {
			structure.remove();
		}
		if (team) {
			team.remove();
		}
		if (token) {
			token.remove();
		}
	}
}
